import { AfterViewInit, Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePickerPresets, GlobalFunctions, makeDateSmall, SnackType } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService } from '@services/authentication.service';
import { Rule, RulesDataService } from '@services/rules-data.service';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, take } from 'rxjs';
import { convertEpochToDateString } from '@functions/functions'
import { EventsDataService, RuleEvent, OfflineEvent, WebhookEvent, DeviationEvent } from '@services/events-data.service';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { ModelsService, ModelType } from '@services/models.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridComponent, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { QueryParamType } from '@global/iFiltering';

/** FilterData */
export interface FilterData {

	selectedTab?: string;

	from?: number;
	to?: number;
	Acknowledged?: boolean;
	Unacknowledged?: boolean;

	CMMS?: boolean;
	notCMMS?: boolean;

	Unarchived?: boolean;
	Archived?: boolean;
	Location?: string;

	eventsFilter?: string;
	eventspageIndex?: number;
	eventspageSize?: number;
	eventsSortColumn?: string;
	eventsSortDirection?: SortDirection;

	offlineFilter?: string;
	offlinepageIndex?: number;
	offlinepageSize?: number;
	offlineSortColumn?: string;
	offlineSortDirection?: SortDirection;

	deviationFilter?: string;
	deviationpageIndex?: number;
	deviationpageSize?: number;
	deviationSortColumn?: string;
	deviationSortDirection?: SortDirection;

	webhookFilter?: string;
	webhookpageIndex?: number;
	webhookpageSize?: number;
	webhookSortColumn?: string;
	webhookSortDirection?: SortDirection;

}

/**
 * Default Class
 */
@Component({
	selector: 'app-events',
	templateUrl: './events.component.html',
	styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnInit, AfterViewInit {

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** offlineEvents */
	public offlineEvents: OfflineEvent[];

	/** Webhook Events */
	public webhookEvents: WebhookEvent[];

	/** Filtered Rule Events */
	public RuleEvents: RuleEvent[];

	/** Deviation Events */
	public deviationEvents: DeviationEvent[];

	/** Rules array */
	public rules: any[] = [];

	/** Asset array */
	public assets: any[] = [];

	public fields: Object = { text: 'RuleText', id: 'RuleEventID' };
	public fieldsOffline: Object = { text: 'OfflineEventText', id: 'OfflineEventID' };
	public fieldsWebhook: Object = { text: 'WebhookEventText', id: 'WebhookEventID' };

	/** Define start date */
	public startDate: Date = new Date();
	/** Define end date */
	public endDate: Date = new Date();
	/** queryFrom */
	private queryFrom: number = 0;
	/** queryTo */
	private queryTo: number = 0;

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** formattedStartDate */
	formattedStartDate: string | undefined;

	/** formattedEndDate */
	formattedEndDate: string | undefined;

	/** selectedTab */
	public selectedTabIndex: number = 0;

	/** tabNames */
	public tabNames = ['Rules', 'Offline', 'Models', "Webhooks"];


	public isRulesLoading: boolean

	public isOfflineLoading: boolean

	public isDeviationsLoading: boolean

	public isWebhooksLoading: boolean

	/** isFiltering */
	public get isFiltering(): boolean {
		return (
			this.dataSourceRule?.filteredData.length != this.dataSourceRule?.data.length ||
			this.dataSourceOffline?.filteredData.length != this.dataSourceOffline?.data.length ||
			this.dataSourceDeviation?.filteredData.length != this.dataSourceDeviation?.data.length ||
			this.dataSourceWebhook?.filteredData.length != this.dataSourceWebhook?.data.length
		);
	}
	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** CMMSFeatureEnabled */
	public CMMSFeatureEnabled: boolean = false;

	/** displayedColumns */
	displayedColumnsRule: string[] = [];
	/** displayedColumns */
	displayedColumns: string[] = ['timestamp', 'rule', 'comments', 'acknowledged', 'CaseID', 'archiveButton', 'Action', 'GoToEvent'];

	/** displayedColumns */
	displayedColumnsOffline: string[] = ['timestamp', 'sensor', 'acknowledged', 'GoToEvent'];

	/** displayedColumns */
	displayedColumnsDeviation: string[] = ['timestamp', 'model', 'asset', 'zscore', 'acceptableDeviation', 'acknowledged', 'Action', 'GoToEvent'];

	/** displayedColumns */
	displayedColumnsWebhook: string[] = ['timestamp', 'webhook', 'type', 'GoToEvent'];

	/** datasource */
	dataSourceRule: MatTableDataSource<RuleEvent>;

	/** datasource */
	dataSourceOffline: MatTableDataSource<OfflineEvent>;

	/** datasource */
	dataSourceDeviation: MatTableDataSource<DeviationEvent>;

	/** datasource */
	dataSourceWebhook: MatTableDataSource<WebhookEvent>;

	private _paginatorRule: MatPaginator;

	@ViewChild('paginatorRule')
	set paginatorRule(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterData.eventspageSize) { paginator.pageSize = this.filterData.eventspageSize }
			if (this.filterData.eventspageIndex) { paginator.pageIndex = this.filterData.eventspageIndex }

			this._paginatorRule = paginator;
			if (this.dataSourceRule) {
				this.dataSourceRule.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Rule paginator:', error);
		}
	}

	private _paginatorOffline: MatPaginator;

	@ViewChild('paginatorOffline')
	set paginatorOffline(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterData.offlinepageSize) { paginator.pageSize = this.filterData.offlinepageSize }
			if (this.filterData.offlinepageIndex) { paginator.pageIndex = this.filterData.offlinepageIndex }

			this._paginatorOffline = paginator;
			if (this.dataSourceOffline) {
				this.dataSourceOffline.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Offline paginator:', error);
		}
	}

	private _paginatorDeviation: MatPaginator;

	@ViewChild('paginatorDeviation')
	set paginatorDeviation(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterData.deviationpageSize) { paginator.pageSize = this.filterData.deviationpageSize }
			if (this.filterData.deviationpageIndex) { paginator.pageIndex = this.filterData.deviationpageIndex }

			this._paginatorDeviation = paginator;
			if (this.dataSourceDeviation) {
				this.dataSourceDeviation.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Deviation paginator:', error);
		}
	}

	private _paginatorWebhook: MatPaginator;

	@ViewChild('paginatorWebhook')
	set paginatorWebhook(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterData.webhookpageSize) { paginator.pageSize = this.filterData.webhookpageSize }
			if (this.filterData.webhookpageIndex) { paginator.pageIndex = this.filterData.webhookpageIndex }

			this._paginatorWebhook = paginator;
			if (this.dataSourceWebhook) {
				this.dataSourceWebhook.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Webhook paginator:', error);
		}
	}

	private _sortRule: MatSort;

	@ViewChild('sortRule')
	set sortRule(sort: MatSort) {
		try {
			if (!sort) { return; }
			this._sortRule = sort;

			if (this.filterData.eventsSortColumn) { sort.active = this.filterData.eventsSortColumn }
			if (this.filterData.eventsSortDirection) { sort.direction= this.filterData.eventsSortDirection }

			if (this.dataSourceRule) {
				this.dataSourceRule.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Rule sort:', error);
		}
	}

	private _sortOffline: MatSort;

	@ViewChild('sortOffline')
	set sortOffline(sort: MatSort) {
		try {
			if (!sort) { return; }

			if (this.filterData.offlineSortColumn) { sort.active = this.filterData.offlineSortColumn }
			if (this.filterData.offlineSortDirection) { sort.direction = this.filterData.offlineSortDirection }

			this._sortOffline = sort;

			if (this.dataSourceOffline) {
				this.dataSourceOffline.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Offline sort:', error);
		}
	}

	private _sortDeviation: MatSort;

	@ViewChild('sortDeviation')
	set sortDeviation(sort: MatSort) {
		try {
			if (!sort) { return; }

			if (this.filterData.deviationSortColumn) { sort.active = this.filterData.deviationSortColumn }
			if (this.filterData.deviationSortDirection) { sort.direction = this.filterData.deviationSortDirection }

			this._sortDeviation = sort;

			if (this.dataSourceDeviation) {
				this.dataSourceDeviation.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Deviation sort:', error);
		}
	}

	private _sortWebhook: MatSort;

	@ViewChild('sortWebhook')
	set sortWebhook(sort: MatSort) {
		try {
			if (!sort) { return; }

			if (this.filterData.webhookSortColumn) { sort.active = this.filterData.webhookSortColumn }
			if (this.filterData.webhookSortDirection) { sort.direction = this.filterData.webhookSortDirection }

			this._sortWebhook = sort;

			if (this.dataSourceWebhook) {
				this.dataSourceWebhook.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Webhook sort:', error);
		}
	}

	/** filters */
	public filterData: FilterData = {};

	/** eventFilterTypes */
	public eventFilterTypes: Record<keyof FilterData, QueryParamType> = {

		from: 'number',
		to: 'number',

		Acknowledged: 'boolean',
		Unacknowledged: 'boolean',

		CMMS: 'boolean',
		notCMMS: 'boolean',

		Unarchived: 'boolean',
		Archived: 'boolean',

		eventsFilter: 'string',
		eventspageIndex: 'number',
		eventspageSize: 'number',
		eventsSortColumn: 'string',
		eventsSortDirection: 'string',

		offlineFilter: 'string',
		offlinepageIndex: 'number',
		offlinepageSize: 'number',
		offlineSortColumn: 'string',
		offlineSortDirection: 'string',

		deviationFilter: 'string',
		deviationpageIndex: 'number',
		deviationpageSize: 'number',
		deviationSortColumn: 'string',
		deviationSortDirection: 'string',

		webhookFilter: 'string',
		webhookpageIndex: 'number',
		webhookpageSize: 'number',
		webhookSortColumn: 'string',
		webhookSortDirection: 'string',

		Location: 'string',
		selectedTab: 'string'

	};

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** flattenedAssetIDs */
	public flattenedAssetIDs: number[] = [];

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** MainLocations */
	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Uid" };

	/** selectedLocation */
	public selectedLocation?: OrganisationsHierarchy;

	private _locationDropdown: DropDownList;
	/** locationDropdown */
	@ViewChild('locationDropdown')
	set locationDropdown(dropdown: DropDownList) {
		try {
			if (!dropdown) { return; }
			this._locationDropdown = dropdown;
			dropdown.showClearButton = true;
		} catch (error) {
			console.error('Error assigning location Dropdown:', error);
		}
	}

	/** modelTypes */
	public modelTypes: ModelType[] = [];

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;


	selectionOptions = { mode: 'Row', type: 'Single' };
	// toolbarOptions = ['ColumnChooser'];
	toolbarOptions = [''];

	filterSettings = { type: 'CheckBox' };

	// groupOptions = { showGroupedColumn: false };
	groupOptions = {};

	pageSettings = { pageSize: 10 };

	@ViewChild('gridEvents')
	public gridEvents?: GridComponent;

	/**
	 * Default Constructor
	 * @param rulesDataService rulesDataService
	 * @param assetsDataService assetsDataService
	 * @param eventsDataService eventsDataService
	 * @param modelsService modelsService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param route Activated Route
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		public rulesDataService: RulesDataService,
		public assetsDataService: AssetsDataService,
		public eventsDataService: EventsDataService,
		public modelsService: ModelsService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {

	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {

		// this.globalFunctions.displaySpinner(true);

		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(distinctUntilChanged()),
			this.route.paramMap
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([hierarchicalData, queryParamMap, paramMap]) =>
				!!hierarchicalData && !!queryParamMap && !!paramMap),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async ([hierarchicalData, queryParamMap, paramMap]) => {

				try {
					let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
						combineLatest([
							this.siteSubscriptions.selectedAccount$.pipe(take(1)),
							this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
							this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
							this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
						]
						)
					);

					/** Validate we are on the correct account */
					if (selectedAccountGUID != paramMap.get('account') ||
						selectedOrganisationUID != paramMap.get('organisation')) {
						alert("Invalid Access to this Page");
						this.router.navigate(["."])
						return;
					}

					this.accountID = selectedAccountGUID;
					this.selectedAccount = selectedAccount;
					this.OrganisationUID = selectedOrganisationUID;

					/** Default to look for only open cases */
					this.filterData = this.globalFunctions.queryParamsToObject<FilterData>(
						queryParamMap,
						this.eventFilterTypes
					);

					this.filterData.Acknowledged = this.filterData.Acknowledged ?? false;
					this.filterData.Unacknowledged = this.filterData.Unacknowledged ?? false;
					this.filterData.Archived = this.filterData.Archived ?? false;
					this.filterData.Unarchived = this.filterData.Unarchived ?? true;


					if (this.filterData.from) {
						this.filterData.to = this.filterData.to ?? 0
						this.startDate = new Date(this.filterData.from);
						this.endDate = new Date(this.filterData.to ?? 0);
					} else {
						this.startDate = new Date(this.today)
						this.endDate = new Date(this.today)

						this.startDate.setDate(this.startDate.getDate() - 30);
						this.endDate.setDate(this.endDate.getDate() + 1)

						this.filterData.from = this.startDate.getTime();
						this.filterData.to = this.endDate.getTime();
					}

					this.CMMSFeatureEnabled = AccountsService.checkFeature(AccountFeatures.SAP, this.selectedAccount);
					if (!this.CMMSFeatureEnabled) { this.displayedColumnsRule = this.displayedColumns.filter(column => column !== 'CaseID'); }
					else { this.displayedColumnsRule = this.displayedColumns; }

					let urlAccountID = this.router.url.split('/')[1];

					if (this.accountID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('event') && !this.router.url.includes('routing#')) {
						this.router.navigateByUrl(`routing#${this.router.url}`);
					}

					this.hierarchy = JSON.parse(hierarchicalData);
					this.MainLocations = [...(this.hierarchy[0].SubChild ?? [])];

					if (this.filterData.Location) {
						this.selectedLocation = this.MainLocations.find(
							subchild => this.filterData.Location == subchild.Uid
						);
					}
					else {
						this.selectedLocation = this.hierarchy[0]
					}

					if (this.selectedLocation) {
						this.assets = this.flattenAssets([this.selectedLocation])
						this.flattenedAssetIDs = this.assets.map(asset => asset.AssetID);
					}

					let tabindex = this.tabNames.indexOf(this.filterData.selectedTab!)
					if (tabindex < 0) { tabindex = 0; }
					this.selectedTabIndex = tabindex;

					if (this.queryTo != this.filterData.to || this.queryFrom != this.filterData.from) {
						this.globalFunctions.displaySpinner(true);
						Promise.all([
							this.getRuleEvents(this.startDate.getTime(), this.endDate.getTime()),
							this.getOfflineEvents(this.startDate.getTime(), this.endDate.getTime()),
							this.getWebhookEvents(this.startDate.getTime(), this.endDate.getTime()),
							this.getDeviationEvents(this.startDate.getTime(), this.endDate.getTime())
						]).finally(() => {
							this.globalFunctions.displaySpinner(false);
						});
					}

					this.queryTo = this.filterData.to;
					this.queryFrom = this.filterData.from ?? 0;
					this.formattedStartDate = makeDateSmall(new Date(this.queryFrom));
					this.formattedEndDate = makeDateSmall(new Date(this.queryTo));
				}
				catch (error) {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				} finally {
					// this.globalFunctions.displaySpinner(false);
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				this.globalFunctions.displaySpinner(false);
			}
		});

	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit(): void { }

	/** filterDescription */
	public get filterDescription(): string {
		const filters: string[] = [];

		// Acknowledgement filters - tabs 0, 1 and 2
		if ([0, 1, 2].includes(this.selectedTabIndex)) {
			if (this.filterData.Acknowledged) {
				filters.push('Acknowledged');
			}

			if (this.filterData.Unacknowledged) {
				filters.push('Unacknowledged');
			}
		}

		// SAP Case / Archive filters - tab 0 only
		if (this.selectedTabIndex === 0) {
			if (this.filterData.CMMS) {
				filters.push('SAP Case');
			}

			if (this.filterData.notCMMS) {
				filters.push('Not SAP Case');
			}

			if (this.filterData.Archived) {
				filters.push('Archived');
			}

			if (this.filterData.Unarchived) {
				filters.push('Not Archived');
			}
		}

		if (filters.length == 0) {
			filters.push('All');
		}

		// Base description
		let description = filters.length
			? `Showing ${filters.join(', ')} Events`
			: 'Showing Events';

		// Location
		if (this.filterData.Location) {
			const location = this.MainLocations?.find(
				location => location.Uid === this.filterData.Location
			);

			description += ` for ${location?.Name ?? this.filterData.Location}`;
		}

		// Date range
		if (this.formattedStartDate && this.formattedEndDate) {
			const dateRange = this.formattedStartDate === this.formattedEndDate
				? this.formattedStartDate
				: `${this.formattedStartDate} - ${this.formattedEndDate}`;

			description += ` from ${dateRange}`;
		} else if (filters.length === 0 && !this.filterData.Location) {
			description += ' from the Past 30 days';
		}

		return description;
	}

	/**
	 * getRuleEvents
	 * @param timeFrom timeFrom  
	 * @param timeTo timeTo 
	 */
	async getRuleEvents(timeFrom: number = 0, timeTo: number = 0) {

		try {
			this.isRulesLoading = true;
			const ruleEvents = await firstValueFrom(
				this.eventsDataService.getRuleEvents$(this.accountID, this.OrganisationUID, 0, 0, timeFrom, timeTo)
			);

			if (ruleEvents && this.flattenedAssetIDs) {
				this.RuleEvents = ruleEvents.filter((x: RuleEvent) => this.flattenedAssetIDs.includes(x.AssetID));
			}

		}
		catch (error: any) {
			this.RuleEvents = [];
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getRuleEvents$", false); }
		} finally {
			this.assignRuleEventsTable();
			this.filterRules();
			this.isRulesLoading = false;
		}

	}

	/**
	 * getOfflineEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	async getOfflineEvents(timeStampFrom: number = 0, timeStampTo: number = 0) {

		try {
			this.isOfflineLoading = true;
			const Events = await firstValueFrom(
				this.eventsDataService.getOfflineEvents$(this.accountID, this.OrganisationUID, 0, timeStampTo, timeStampFrom)
			);

			if (Events) {
				this.offlineEvents = Events.filter((x: OfflineEvent) => this.checkContainedAssetIDs(x.AssetIDs) == true);
			}

		}
		catch (error: any) {
			this.offlineEvents = [];
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getWebhookEvents$", false); }
		} finally {
			this.assignOfflineEventsTable();
			this.filterRules();
			this.isOfflineLoading = false;
		}

	}

	/**
	 * getDeviationEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	async getDeviationEvents(timeStampFrom: number = 0, timeStampTo: number = 0) {

		try {
			this.isDeviationsLoading = true;
			const Events = await firstValueFrom(
				this.eventsDataService.getDeviationEvents$(this.accountID, this.OrganisationUID, 0, 0, timeStampFrom, timeStampTo)
			);

			if (Events) {
				this.deviationEvents = Events.filter((x: DeviationEvent) => this.flattenedAssetIDs.includes(x.AssetID));
			}

		}
		catch (error: any) {
			this.deviationEvents = [];
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getDeviationEvents$", false); }
		} finally {
			this.assignDeviationEventsTable();
			this.filterRules();
			await this.getModelTypes();
			this.isDeviationsLoading = false;
		}

	}

	/**
	 * getWebhookEvents
	 * @param timeStampFrom timeStampFrom 
	 * @param timeStampTo timeStampTo 
	 */
	async getWebhookEvents(timeStampFrom: number = 0, timeStampTo: number = 0) {

		try {
			this.isWebhooksLoading = true;
			const Events = await firstValueFrom(
				this.eventsDataService.getWebhookEvents$(this.accountID, this.OrganisationUID, 0, timeStampTo, timeStampFrom)
			);

			if (Events) {
				this.webhookEvents = Events;
			}

		}
		catch (error: any) {
			this.webhookEvents = [];
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.getWebhookEvents$", false); }
		} finally {
			this.assignWebhookEventsTable();
			this.filterRules();
			this.isWebhooksLoading = false;
		}

	}

	/** assignRuleEventsTable */
	assignRuleEventsTable() {

		try {
			if (!this.dataSourceRule) {
				this.dataSourceRule = new MatTableDataSource(this.RuleEvents);
			} else {
				this.dataSourceRule.data = this.RuleEvents;
			}

		} catch (error) {
			console.log(error);
		}

		// if (this.gridEvents) {
		// 	// this.gridEvents.dataSource = [];
		// 	this.gridEvents.dataSource = this.RuleEvents;
		// 	this.gridEvents.dataBind()
		// 	this.gridEvents.refresh()
		// }

		this.dataSourceRule.filterPredicate = (data: RuleEvent, filter: string) => {

			const Acknowledged = this.filterData.Acknowledged === true;
			const Unacknowledged = this.filterData.Unacknowledged === true;

			const CMMS = this.filterData.CMMS === true;
			const notCMMS = this.filterData.notCMMS === true;

			const Archived = this.filterData.Archived === true;
			const Unarchived = this.filterData.Unarchived === true;


			if (!this.flattenedAssetIDs.includes(data.AssetID)) {
				return false
			}

			const filterAcknowledged = Acknowledged || Unacknowledged;
			if (
				filterAcknowledged &&
				!(
					(Acknowledged && data.Acknowledged) ||
					(Unacknowledged && !data.Acknowledged)
				)
			) {
				return false;
			}

			const filterCMMS = CMMS || notCMMS;
			if (
				filterCMMS &&
				!(
					(CMMS && data.CaseID) ||
					(notCMMS && !data.CaseID)
				)
			) {
				return false;
			}

			const filterArchived = Archived || Unarchived;
			if (
				filterArchived &&
				!(
					(Archived && data.Archived) ||
					(Unarchived && !data.Archived)
				)
			) { return false; }

			const query = filter.toLowerCase().trim();
			const dateStr = `${convertEpochToDateString(data.AcknowledgedTimestamp)}`.toLowerCase();
			const RuleName = `${data.RuleName}`.toLowerCase() || '';
			const AssetName = `${data.AssetName}`.toLowerCase() || '';
			const Comment = `${data.Comments}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${AssetName} ${Comment} ${RuleName}`;
			return combinedStr.includes(query);

		};


		this.dataSourceRule.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'timestamp': return item.TimeStamp || ''; // Ensure no null/undefined values
				case 'rule': return item.RuleID || '';
				case 'comments': return item.Comments || '';
				case 'CaseID': return item.CaseID || '';
				case 'acknowledged': return item.Acknowledged ? 'True' : 'False';
				default: return '';
			}
		};

	}

	/** assignRuleEventsTable */
	assignOfflineEventsTable() {

		if (!this.dataSourceOffline) {
			this.dataSourceOffline = new MatTableDataSource(this.offlineEvents);
		} else {
			this.dataSourceOffline.data = this.offlineEvents;
		}

		this.dataSourceOffline.filterPredicate = (data: OfflineEvent, filter: string) => {

			const Acknowledged = this.filterData.Acknowledged === true;
			const Unacknowledged = this.filterData.Unacknowledged === true;
			const Archived = this.filterData.Archived === true;
			const Unarchived = this.filterData.Unarchived === true;

			const filterAcknowledged = Acknowledged || Unacknowledged;
			if (
				filterAcknowledged &&
				!(
					(Acknowledged && data.Acknowledged) ||
					(Unacknowledged && !data.Acknowledged)
				)
			) {
				return false;
			}

			const query = filter.toLowerCase().trim();
			const dateStr = `${convertEpochToDateString(data.AcknowledgedTimestamp)}`.toLowerCase();
			const SensorName = `${data.SensorName}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${Comment} ${SensorName}`;
			return combinedStr.includes(query);

		};

		this.dataSourceOffline.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'SensorName': return item.SensorName || ''; // Ensure no null/undefined values
				case 'TimeStamp': return item.TimeStamp || '';
				default: return '';
			}
		};

	}

	/** assignDeviationEventsTable */
	assignDeviationEventsTable() {

		if (!this.dataSourceDeviation) {
			this.dataSourceDeviation = new MatTableDataSource(this.deviationEvents);
		} else {
			this.dataSourceDeviation.data = this.deviationEvents;
		}

		this.dataSourceDeviation.filterPredicate = (data: DeviationEvent, filter: string) => {

			const Acknowledged = this.filterData.Acknowledged === true;
			const Unacknowledged = this.filterData.Unacknowledged === true;
			const Archived = this.filterData.Archived === true;
			const Unarchived = this.filterData.Unarchived === true;

			const filterAcknowledged = Acknowledged || Unacknowledged;
			if (
				filterAcknowledged &&
				!(
					(Acknowledged && data.Acknowledged) ||
					(Unacknowledged && !data.Acknowledged)
				)
			) {
				return false;
			}

			const query = filter.toLowerCase().trim();
			const dateStr = `${convertEpochToDateString(data.Timestamp)}`.toLowerCase();
			const Message = `${data.Message}`.toLowerCase() || '';
			const AssetName = `${data.AssetName}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${Message} ${AssetName}`;
			return combinedStr.includes(query);

		};

		this.dataSourceDeviation.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'AssetName': return item.AssetName || ''; // Ensure no null/undefined values
				case 'Message': return item.Message || '';
				case 'Timestamp': return item.Timestamp || '';
				default: return '';
			}
		};

	}

	/** assignWebhookEventsTable */
	assignWebhookEventsTable() {

		if (!this.dataSourceWebhook) {
			this.dataSourceWebhook = new MatTableDataSource(this.webhookEvents);
		} else {
			this.dataSourceWebhook.data = this.webhookEvents;
		}

		this.dataSourceWebhook.filterPredicate = (data: WebhookEvent, filter: string) => {

			const Acknowledged = this.filterData.Acknowledged === true;
			const Unacknowledged = this.filterData.Unacknowledged === true;
			const Archived = this.filterData.Archived === true;
			const Unarchived = this.filterData.Unarchived === true;

			const query = filter.toLowerCase().trim();
			const dateStr = `${convertEpochToDateString(data.TimeStamp)}`.toLowerCase();
			const ClientWebhookID = `${data.ClientWebhookID}`.toLowerCase() || '';
			const Description = `${data.Description}`.toLowerCase() || '';

			// Combine the necessary fields for filtering
			const combinedStr = `${dateStr} ${ClientWebhookID} ${Description}`;
			return combinedStr.includes(query);

		};

		this.dataSourceWebhook.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'Description': return item.Description || ''; // Ensure no null/undefined values
				case 'TimeStamp': return item.TimeStamp || '';
				case 'ClientWebhookID': return item.ClientWebhookID || '';
				default: return '';
			}
		};

	}

	/**
	 * tabChange
	 * @param event event  
	 */
	tabChange(event: any) {

		this.selectedTabIndex = event.selectedIndex;
		this.filterData.selectedTab = this.tabNames[event.selectedIndex] ?? ""
		this.rebuildURL(false);

	}

	/** returns true if any of the provided assetIDs are contained
	 * 	withing the flattened assets available to this user
	 */
	checkContainedAssetIDs(assetIDs?: string): boolean {
		let result: boolean = false;
		if (assetIDs) {
			let arrIDs = assetIDs.split(",");
			arrIDs.forEach(assetID => {
				if (this.flattenedAssetIDs.includes(parseInt(assetID))) {
					result = true;
				}
			})
		}
		return result;
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * onFilterChange
	 * Handles changes to the filter checkboxes and updates the filtered cases accordingly.
	 * When the "All" filter is selected, it deselects the "Open", "Closed", and "In CMMS" filters.
	 * When any of the "Open", "Closed", or "In CMMS" filters are changed, it deselects the "All" filter if necessary.
	 * After updating the filter states, it calls the filterCases() method to apply the filters to the case list.
	 * @param changed 
	 */
	onFilterChange(changed: keyof typeof this.filterData) {

		if (this.paginatorRule) { this.paginatorRule.pageIndex = 0; }
		if (this.paginatorOffline) { this.paginatorOffline.pageIndex = 0; }
		if (this.paginatorDeviation) { this.paginatorDeviation.pageIndex = 0; }
		if (this.paginatorWebhook) { this.paginatorWebhook.pageIndex = 0; }

		this.filterRules();
	}

	rowSelected(args: RowSelectEventArgs): void {

		console.log(args);
		let data = <RuleEvent>args.data
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.RuleEventID}`], {
			queryParams: { eventType: '2' }
		});
	}

	/**
	 * selectEvent
	 * @param data
	 */
	selectEvent(data: RuleEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.RuleEventID}`], {
			queryParams: { eventType: '2' }
		});
	}

	selectOfflineEvent(data: OfflineEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.OfflineEventID}`], {
			queryParams: { eventType: '1' }
		});
	}

	selectWebhookEvent(data: WebhookEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.WebhookEventID}`], {
			queryParams: { eventType: '3' }
		});
	}

	selectDeviationEvent(data: DeviationEvent) {
		this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/event/${data.DeviationEventID}`], {
			queryParams: { eventType: '4' }
		});
	}

	/** getRule */
	getRule(ruleID: number): Rule | undefined {
		return this.rules.find((x: Rule) => x.RuleID == ruleID);
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * splitValues
	 * @param text
	 * @returns
	 */
	splitValues(text: string): string[] { return text.split(';') }

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes
	 * @returns
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * webhookEventTypeToText
	 * @param type 
	 * @returns 
	 */
	webhookEventTypeToText(type: number): string {
		switch (type) {
			case 0:
				return "Webhook Offline"
			case 1:
				return "Webhook Offline"
			default:
				return ""
		}
	}

	/**
	 * onFilteringRuleEvents
	 * @param event
	 */
	onFilteringRuleEvents(event: any): void {

		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filterData.eventsFilter = query;
		this.dataSourceRule.filter = query;
		this.filterRules("Events");
	}

	/**
	 * onFilteringOfflineEvents
	 * @param event
	 */
	onFilteringOfflineEvents(event: any): void {

		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filterData.offlineFilter = query;
		this.dataSourceOffline.filter = query;
		this.filterRules("Offline");

	}

	/**
	 * onFilteringWebhookEvents
	 * @param event
	 */
	onFilteringWebhookEvents(event: any): void {

		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filterData.webhookFilter = query;
		this.dataSourceWebhook.filter = query;
		this.filterRules("Webhooks");

	}

	/**
	 * onFilteringDeviationEvents
	 * @param event 
	 */
	onFilteringDeviationEvents(event: any): void {

		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filterData.deviationFilter = query;
		this.dataSourceDeviation.filter = query;
		this.filterRules("Deviation");

	}

	/**
	 * onEventSortChange
	 * @param event 
	 */
	public onEventSortChange(event: Sort): void {
		this.filterData.eventsSortColumn= event.active;
		this.filterData.eventsSortDirection = event.direction;

		this.rebuildURL(false);
	}

	/**
	 * onOfflineSortChange
	 * @param event 
	 */
	public onOfflineSortChange(event: Sort): void {
		this.filterData.offlineSortColumn = event.active;
		this.filterData.offlineSortDirection = event.direction;

		this.rebuildURL(false);
	}

	/**
	 * onDeviationSortChange
	 * @param event 
	 */
	public onDeviationSortChange(event: Sort): void {
		this.filterData.deviationSortColumn = event.active;
		this.filterData.deviationSortDirection = event.direction;

		this.rebuildURL(false);
	}

	/**
	 * onWebhookSortChange
	 * @param event 
	 */
	public onWebhookSortChange(event: Sort): void {
		this.filterData.webhookSortColumn = event.active;
		this.filterData.webhookSortDirection = event.direction;

		this.rebuildURL(false);
	}

	/**
	 * filterRules
	 * Applies the selected filters to the data and updates the filtered results accordingly.
	 */
	filterRules(page: string = "All", reQuery: boolean = false) {

		if ((page == "Events" || page == "All") && this.dataSourceRule) {
			const currentFilter = this.dataSourceRule.filter;
			this.dataSourceRule.filter = '';
			this.dataSourceRule.filter = currentFilter || this.filterData.eventsFilter || ' ';
		}

		if ((page == "Offline" || page == "All") && this.dataSourceOffline) {
			const currentFilter = this.dataSourceOffline.filter;
			this.dataSourceOffline.filter = '';
			this.dataSourceOffline.filter = currentFilter || this.filterData.offlineFilter || ' ';
		}

		if ((page == "Deviation" || page == "All") && this.dataSourceDeviation) {
			const currentFilter = this.dataSourceDeviation.filter;
			this.dataSourceDeviation.filter = '';
			this.dataSourceDeviation.filter = currentFilter || this.filterData.deviationFilter || ' ';
		}

		if ((page == "Webhooks" || page == "All") && this.dataSourceWebhook) {
			const currentFilter = this.dataSourceWebhook.filter;
			this.dataSourceWebhook.filter = '';
			this.dataSourceWebhook.filter = currentFilter || this.filterData.webhookFilter || ' ';
		}

		this.rebuildURL(reQuery);
	}


	/**
	 * DatePickerChange
	 * @param event
	 */
	DatePickerChange(event: any) {
		try {
			event.startDate.setHours(0, 0, 0, 0);
			event.endDate.setHours(0, 0, 0, 0);

			if (event.daySpan == 0) {
				this.startDate.setDate(this.today.getDate() - 30);
				this.endDate = this.today;
				this.filterData.to = this.endDate.getTime();
				this.filterData.from = this.startDate.getTime();

				if (this.queryTo != this.filterData.to || this.queryFrom != this.filterData.from) {
					this.filterRules("All", true);
				}
				return
			}

			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				this.startDate = event.startDate;
				this.endDate = event.endDate

				if (this.startDate && this.endDate) {
					this.formattedStartDate = makeDateSmall(this.startDate);
					this.formattedEndDate = makeDateSmall(this.endDate);
					/** Add a day to retrieve data for last day too */
					this.endDate.setDate(this.endDate.getDate() + 1)

					this.filterData.from = this.startDate.getTime();
					this.filterData.to = this.endDate.getTime();
					if (this.queryTo != this.filterData.to || this.queryFrom != this.filterData.from) {
						this.filterRules("All", true);
					}
				}
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }

	}

	/**
	 * makeDateSmall
	 * @param date date 
	 * @returns string
	 */
	makeDateSmall(date: Date): string {
		const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
		const dayOfWeek = daysOfWeek[date.getDay()];
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${dayOfWeek} ${day}/${month}/${year}`;

	}

	/**
	 * resetQueryDates
	 */
	resetQueryDates() {
		this.formattedStartDate = undefined;
		this.formattedEndDate = undefined;
		this.queryFrom = 0;
		this.queryTo = 0;

		this.filterData = {
			Unarchived: true
		};

		this.rebuildURL(true);
	}


	/**
	 * Rebuild an URL for refreshing page
	 * @param start Start Date
	 * @param end End Date
	 * @param sensors Array of sensors
	 */
	// rebuildURL(start: any = this.paramStartDate.getTime(), end: any = this.paramEndDate.getTime()): void {
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			// this.router.navigate([], { queryParams: this.filterData, queryParamsHandling: 'merge' });
			this.router.navigate([], { queryParams: this.filterData });
		} else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterData,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}

	}

	/** applyFilter */
	applyFilterRules(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSourceRule.filter = filterValue.trim().toLowerCase();

		if (this.dataSourceRule.paginator) {
			this.dataSourceRule.paginator.firstPage();
		}
	}

	/**
	 * archiveRuleEvent
	 * @param ruleEvent ruleEvent  
	 */
	async archiveRuleEvent(ruleEvent: RuleEvent, archive: boolean = true) {

		if (ruleEvent.Acknowledged) {
			try {

				const response = await firstValueFrom(
					this.eventsDataService.archiveRuleEvent$(this.accountID, this.OrganisationUID, ruleEvent.RuleEventID, archive)
				);

				if (response) {
					ruleEvent.Archived = archive;
					// this.getRuleEvents();
					this.globalFunctions.DisplaySnackBar({
						message: `Event ${archive ? 'Archived' : 'Restored'}`,
						duration: 2000,
						type: SnackType.Info,
						action: ""
					})
				}

			}
			catch (error: any) {
				if (error.status == 404) { }
				else { this.globalFunctions.HandleError(error, "Error: this.eventsDataService.archiveRuleEvent$", false); }
			}

		} else {
			this.globalFunctions.DisplaySnackBar({
				message: "Cannot Archive an Unacknowledged Event",
				duration: 2000,
				type: SnackType.Error,
				action: ""
			})
		}
	}

	/**
	 * findAsset
	 * @param assetID assetID  
	 * @returns 
	 */
	getAsset(assetID: number): LevelAsset | undefined {
		return this.assets.find((x: LevelAsset) => x.AssetID == assetID);
	}

	/**
	 * goToAsset
	 * @param row 
	 */
	goToAsset(row: any) {
		let asset = this.getAsset(row.AssetID);
		if (asset) {
			this.siteSubscriptions.SetSelectedAsset(asset);
			this.router.navigate([`./${this.accountID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
		}
	}

	selectCase(ruleEvent: RuleEvent) {
		this.router.navigate([
			this.route.snapshot.paramMap.get('account'),
			'organisation',
			this.route.snapshot.paramMap.get('organisation'),
			'SAPCases',
			ruleEvent.CaseID
		]);
	}

	/**
	 * getDate
	 * @param epoch epoch  
	 * @returns 
	 */
	getDate(epoch: number): string {
		let date: Date = new Date(Number(epoch));
		return this.getShortDate(date);
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * filterByLocation
	 * @param event event 
	 * @param cancel cancel 
	 */
	filterByLocation(event: any, cancel?: boolean) {

		if (this.locationDropdown) { this.locationDropdown.showClearButton = true; }

		if (cancel) {
			this.selectedLocation = this.hierarchy[0];
			this.filterData.Location = undefined;
			event.value == "";
		} else if (event.itemData) {
			this.selectedLocation = <OrganisationsHierarchy>event.itemData;
			this.filterData.Location = event.value;
		} else if (!event.itemData) {
			this.selectedLocation = this.hierarchy[0];
			this.filterData.Location = undefined;
		}

		if (this.selectedLocation) {
			this.assets = this.flattenAssets([this.selectedLocation])
			this.flattenedAssetIDs = this.assets.map(asset => asset.AssetID);
		}

		this.filterRules();
	}

	/**
	 * getModelTypes
	 */
	async getModelTypes() {

		try {

			const modelTypes = await firstValueFrom(
				this.modelsService.getModelTypes$(this.accountID, this.OrganisationUID)
			);

			if (modelTypes) {
				this.modelTypes = modelTypes;
			}

		}
		catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: this.modelsService.getModelTypes$", false); }
		} finally { }

	}

	/**
	 * getModelTypeName
	 * @param modelTypeID modelTypeID  
	 * @returns 
	 */
	getModelTypeName(modelTypeID: number): string {
		let result: string = "";
		let modelType = this.modelTypes.find((x: ModelType) => x.ModelTypeID == modelTypeID);
		if (modelType) {
			result = modelType.ModelName;
		}
		return result;
	}

	/**
	 * onPageChange
	 */
	onPageChange(pag: MatPaginator, event: PageEvent): void {

		if (pag == this._paginatorRule) {
			this.filterData.eventspageSize = pag.pageSize;
			this.filterData.eventspageIndex = pag.pageIndex;
		}

		if (pag == this._paginatorOffline) {
			this.filterData.offlinepageSize = pag.pageSize;
			this.filterData.offlinepageIndex = pag.pageIndex;
		}

		if (pag == this._paginatorDeviation) {
			this.filterData.deviationpageSize = pag.pageSize;
			this.filterData.deviationpageIndex = pag.pageIndex;
		}

		if (pag == this._paginatorWebhook) {
			this.filterData.webhookpageSize = pag.pageSize;
			this.filterData.webhookpageIndex = pag.pageIndex;
		}

		this.filterRules("All", false)
	}
}
